import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  name: '',
  season: '',
  format: 'league',
  start_date: '',
  end_date: '',
  registration_deadline: '',
  max_teams: '16',
  status: 'draft',
  visibility: 'public',
  description: '',
  team_ids: [],
  field_ids: [],
  referee_ids: [],
  round_plan: '15',
  format_notes: '',
};

const STEPS = [
  { id: 0, label: 'Base' },
  { id: 1, label: 'Times' },
  { id: 2, label: 'Rodadas' },
  { id: 3, label: 'Campos' },
  { id: 4, label: 'Arbitragem' },
];

function decodeRoles(user) {
  if (Array.isArray(user?.roles)) {
    return user.roles;
  }
  if (typeof user?.role_codes === 'string' && user.role_codes.trim()) {
    return user.role_codes.split(',').filter(Boolean);
  }
  return [];
}

export function ChampionshipsPage({
  loading,
  error,
  data,
  onCreateChampionship,
  onUpdateChampionship,
  onDeleteChampionship,
  saving,
  deleting,
  canManage,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [step, setStep] = useState(0);

  const rows = data?.items || [];
  const references = data?.references || {};
  const teams = references.teams || [];
  const fields = references.fields || [];
  const referees = (references.users || []).filter((user) => {
    const roles = decodeRoles(user);
    return roles.includes('referee') || roles.includes('admin') || roles.includes('organizer');
  });
  const isEditing = Boolean(editingId);
  const buttonLabel = saving ? 'Salvando...' : isEditing ? 'Atualizar campeonato' : 'Criar campeonato';

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setStep(0);
  };

  const summaryText = useMemo(() => {
    return `${form.team_ids.length} times • ${form.field_ids.length} campos • ${form.referee_ids.length} árbitros`;
  }, [form.field_ids.length, form.referee_ids.length, form.team_ids.length]);

  if (loading) return <LoadingBlock label="Carregando campeonatos..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Wizard de campeonato"
        subtitle="Cadastro guiado com nome, formato, times, rodadas, campos e arbitragem."
        actions={canManage && isEditing ? <button className="ghost-button" onClick={resetForm}>Cancelar edição</button> : null}
      >
        {canManage ? (
          <form
            className="admin-form"
            onSubmit={(event) => {
              event.preventDefault();
              const payload = {
                ...form,
                max_teams: Number(form.max_teams || 16),
                round_plan: Number(form.round_plan || 0),
                setup: {
                  field_ids: form.field_ids.map(Number),
                  referee_ids: form.referee_ids.map(Number),
                  round_plan: Number(form.round_plan || 0),
                  format_notes: form.format_notes,
                },
              };

              if (isEditing) {
                onUpdateChampionship(editingId, payload).then?.(resetForm);
                return;
              }

              onCreateChampionship(payload).then?.(resetForm);
            }}
          >
            <div className="callout-box compact-box">
              <span className="eyebrow">Acesso do administrador</span>
              <strong>Cadastro, edição e exclusão liberados</strong>
              <p>Como admin, você controla nome, formato, times, rodadas, campos e arbitragem da competição.</p>
            </div>
            <div className="wizard-steps">
              {STEPS.map((item) => (
                <button
                  key={item.id}
                  className={`wizard-step ${step === item.id ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => setStep(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {step === 0 && (
              <>
                <div className="form-row">
                  <label>
                    Nome da copa/campeonato
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </label>
                  <label>
                    Temporada
                    <input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} required />
                  </label>
                </div>
                <div className="form-row triple">
                  <label>
                    Formato
                    <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
                      <option value="league">Pontos corridos</option>
                      <option value="groups_knockout">Grupos + mata-mata</option>
                      <option value="knockout">Mata-mata</option>
                      <option value="points">Classificação simples</option>
                    </select>
                  </label>
                  <label>
                    Status
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="draft">Rascunho</option>
                      <option value="published">Publicado</option>
                      <option value="running">Em andamento</option>
                      <option value="finished">Encerrado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </label>
                  <label>
                    Visibilidade
                    <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
                      <option value="public">Público</option>
                      <option value="private">Privado</option>
                    </select>
                  </label>
                </div>
                <div className="form-row triple">
                  <label>
                    Início
                    <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
                  </label>
                  <label>
                    Fim
                    <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
                  </label>
                  <label>
                    Limite de times
                    <input type="number" min="2" value={form.max_teams} onChange={(e) => setForm({ ...form, max_teams: e.target.value })} />
                  </label>
                </div>
                <label>
                  Prazo de inscrição
                  <input type="date" value={form.registration_deadline} onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} />
                </label>
                <label>
                  Descrição
                  <textarea rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </label>
              </>
            )}

            {step === 1 && (
              <div className="selection-grid">
                {teams.map((team) => {
                  const active = form.team_ids.includes(String(team.id));
                  return (
                    <button
                      key={team.id}
                      type="button"
                      className={`selection-card ${active ? 'is-active' : ''}`}
                      onClick={() => setForm({
                        ...form,
                        team_ids: active
                          ? form.team_ids.filter((id) => id !== String(team.id))
                          : [...form.team_ids, String(team.id)],
                      })}
                    >
                      <strong>{team.name}</strong>
                      <span>{team.short_name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <>
                <div className="form-row">
                  <label>
                    Planejamento de rodadas
                    <input type="number" min="1" value={form.round_plan} onChange={(e) => setForm({ ...form, round_plan: e.target.value })} />
                  </label>
                  <label>
                    Resumo do formato
                    <input value={form.format_notes} onChange={(e) => setForm({ ...form, format_notes: e.target.value })} placeholder="Ex.: turno único + semifinal + final" />
                  </label>
                </div>
                <div className="callout-box compact-box">
                  <span className="eyebrow">Resumo parcial</span>
                  <strong>{summaryText}</strong>
                </div>
              </>
            )}

            {step === 3 && (
              <div className="selection-grid">
                {fields.map((field) => {
                  const active = form.field_ids.includes(String(field.id));
                  return (
                    <button
                      key={field.id}
                      type="button"
                      className={`selection-card ${active ? 'is-active' : ''}`}
                      onClick={() => setForm({
                        ...form,
                        field_ids: active
                          ? form.field_ids.filter((id) => id !== String(field.id))
                          : [...form.field_ids, String(field.id)],
                      })}
                    >
                      <strong>{field.name}</strong>
                      <span>{field.city}/{field.state}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 4 && (
              <div className="selection-grid">
                {referees.map((referee) => {
                  const active = form.referee_ids.includes(String(referee.id));
                  return (
                    <button
                      key={referee.id}
                      type="button"
                      className={`selection-card ${active ? 'is-active' : ''}`}
                      onClick={() => setForm({
                        ...form,
                        referee_ids: active
                          ? form.referee_ids.filter((id) => id !== String(referee.id))
                          : [...form.referee_ids, String(referee.id)],
                      })}
                    >
                      <strong>{referee.name}</strong>
                      <span>{decodeRoles(referee).join(', ') || 'sem perfil'}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="inline-actions">
              <button className="ghost-button" type="button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
                Voltar
              </button>
              <button className="ghost-button" type="button" onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={step === STEPS.length - 1}>
                Avançar
              </button>
              <button className="primary-button" type="submit" disabled={saving}>{buttonLabel}</button>
            </div>
          </form>
        ) : (
          <div className="empty-state">Seu perfil pode visualizar campeonatos, mas não pode editar ou criar copas.</div>
        )}
      </SectionCard>

      <SectionCard title="Lista de campeonatos" subtitle="Use editar para retomar o wizard com a configuração já existente.">
        <DataTable
          rows={rows}
          columns={[
            { key: 'name', label: 'Nome' },
            { key: 'season', label: 'Temporada' },
            { key: 'format', label: 'Formato' },
            { key: 'teams_count', label: 'Times' },
            { key: 'start_date', label: 'Início' },
            { key: 'end_date', label: 'Fim' },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'actions',
              label: 'Ações',
              render: (row) => (
                <div className="inline-actions">
                  {canManage ? (
                    <>
                      <button
                        className="inline-button"
                        onClick={() => {
                          const detail = rows.find((item) => item.id === row.id) || row;
                          const setup = detail.setup || {};
                          setEditingId(row.id);
                          setStep(0);
                          setForm({
                            name: row.name || '',
                            season: row.season || '',
                            format: row.format || 'league',
                            start_date: row.start_date || '',
                            end_date: row.end_date || '',
                            registration_deadline: row.registration_deadline || '',
                            max_teams: String(row.max_teams || 16),
                            status: row.status || 'draft',
                            visibility: row.visibility || 'public',
                            description: row.description || '',
                            team_ids: (detail.teams || []).map((team) => String(team.time_id)),
                            field_ids: (setup.field_ids || []).map(String),
                            referee_ids: (setup.referee_ids || []).map(String),
                            round_plan: String(setup.round_plan || '15'),
                            format_notes: setup.format_notes || '',
                          });
                        }}
                      >
                        Editar
                      </button>
                      <button className="inline-button danger-link" onClick={() => onDeleteChampionship(row.id)} disabled={deleting}>
                        Excluir
                      </button>
                    </>
                  ) : (
                    <span className="table-muted">Somente leitura</span>
                  )}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}
